import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    overheadRateOutsideFaculty: { type: Number, default: 11 },
    overheadRateOnFaculty: { type: Number, default: 22 }
  }

  connect() {
    this.recalculate()
  }

  recalculate() {
    const price = this.numberFromInput("preferred_price")
    const expectedStudentCount = this.numberFromInput("expected_student_count")
    const directWageCosts = this.numberFromInput("direct_wage_costs")
    const dppCosts = this.numberFromInput("dpp_costs")
    const directMaterialCosts = this.numberFromInput("direct_material_costs")
    const otherServicesPurchasesTotal = this.numberFromInput("other_services_purchases_total")
    const facultyOverhead = this.facultyOverhead()

    const administrativeCosts = this.multiply(expectedStudentCount, 100, 1.35)
    const totalCourseRevenue = this.multiply(expectedStudentCount, price)
    const overheadCosts11 = this.multiply(totalCourseRevenue, this.overheadRateOutsideFacultyValue / 100)
    const overheadCosts22 = this.multiply(totalCourseRevenue, this.overheadRateOnFacultyValue / 100)
    const selectedOverheadCosts = facultyOverhead ? overheadCosts22 : overheadCosts11
    const totalCostsWithoutOverhead = this.sum(
      this.multiply(directWageCosts, 1.353),
      dppCosts,
      directMaterialCosts,
      otherServicesPurchasesTotal,
      administrativeCosts
    )
    const totalRealizationCosts = this.sum(totalCostsWithoutOverhead, selectedOverheadCosts)
    const minimumStudentCount = this.ceiling(this.divide(this.sum(administrativeCosts, selectedOverheadCosts), price))
    const priceWithinStudyProgram = this.sum(price, 100)
    const priceOutsideStudyProgram = this.sum(price, 300)
    const profitAvailableResources = this.subtract(totalCourseRevenue, totalRealizationCosts)

    this.writeOutput("administrative_costs", administrativeCosts)
    this.writeOutput("total_costs_without_overhead", totalCostsWithoutOverhead)
    this.writeOutput("overhead_costs_11", overheadCosts11)
    this.writeOutput("overhead_costs_22", overheadCosts22)
    this.writeOutput("total_course_revenue", totalCourseRevenue)
    this.writeOutput("total_realization_costs", totalRealizationCosts)
    this.writeOutput("expected_student_count", expectedStudentCount)
    this.writeOutput("minimum_student_count", minimumStudentCount)
    this.writeOutput("price_within_study_program", priceWithinStudyProgram)
    this.writeOutput("price_outside_study_program", priceOutsideStudyProgram)
    this.writeOutput("profit_available_resources", profitAvailableResources)
    this.setOutputHidden("overhead_costs_11", facultyOverhead)
    this.setOutputHidden("overhead_costs_22", !facultyOverhead)
    this.syncRevenueWarning(totalCourseRevenue, totalRealizationCosts)
  }

  numberFromInput(key) {
    const element = this.element.querySelector(`[data-calculation-input="${key}"]`)
    if (!element) return null

    const normalizedValue = element.value.trim().replace(",", ".")
    if (normalizedValue.length === 0) return null

    const value = Number(normalizedValue)
    return Number.isFinite(value) ? value : null
  }

  valueFromInput(key) {
    const element = this.element.querySelector(`[data-calculation-input="${key}"]`)
    return element?.value || ""
  }

  facultyOverhead() {
    return ["on_faculty", "vv_stredisko_straz", "vv_stredisko_mulda", "Na fakultě", "VV středisko Stráž", "VV středisko Mulda"].includes(this.valueFromInput("held_at"))
  }

  writeOutput(key, value) {
    const elements = this.element.querySelectorAll(`[data-calculation-output="${key}"]`)
    if (elements.length === 0) return

    const rawValue = value === null ? "" : this.formatNumber(value)
    elements.forEach((element) => {
      if ("value" in element) {
        element.value = rawValue
      } else {
        element.textContent = this.formatDisplayValue(value, element.dataset.calculationOutputFormat)
      }
    })
  }

  sum(...values) {
    if (values.every((value) => value === null)) return null

    return values.reduce((total, value) => total + (value ?? 0), 0)
  }

  multiply(...values) {
    if (values.some((value) => value === null)) return null

    return values.reduce((total, value) => total * value, 1)
  }

  subtract(first, second) {
    if (first === null || second === null) return null

    return first - second
  }

  divide(first, second) {
    if (first === null || second === null || second === 0) return null

    return first / second
  }

  ceiling(value) {
    if (value === null) return null

    return Math.ceil(value)
  }

  setOutputHidden(key, hidden) {
    const elements = this.element.querySelectorAll(`[data-calculation-output="${key}"]`)
    elements.forEach((element) => {
      element.closest("[data-calculation-output-card]")?.toggleAttribute("hidden", hidden)
    })
  }

  syncRevenueWarning(totalCourseRevenue, totalRealizationCosts) {
    const warning = totalCourseRevenue !== null && totalRealizationCosts !== null && totalCourseRevenue < totalRealizationCosts
    const elements = this.element.querySelectorAll(`[data-calculation-output="total_course_revenue"]`)
    elements.forEach((element) => {
      element.closest("[data-calculation-output-card]")?.classList.toggle("calculation-output-card--danger", warning)
    })
  }

  formatNumber(value) {
    return new Intl.NumberFormat("cs-CZ", {
      useGrouping: false,
      maximumFractionDigits: 0
    }).format(Math.round(value))
  }

  formatDisplayValue(value, format) {
    if (value === null) return "—"
    if (format === "currency") return `${this.formatGroupedNumber(value)} Kč`

    return this.formatGroupedNumber(value)
  }

  formatGroupedNumber(value) {
    return new Intl.NumberFormat("cs-CZ", {
      useGrouping: true,
      maximumFractionDigits: 0
    }).format(Math.round(value))
  }
}
